import { css, StyleDeclarationValue, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Control, Controller, FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { THEME } from '../../../../../../web/components-v2/Theme/theme';

interface FormFieldWrapperProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
	name: TName;
	control: Control<TFieldValues>;
	title?: string;
	/** Optional element rendered immediately after the title (e.g. an info icon with a tooltip). */
	titleAdornment?: React.ReactNode;
	description?: string;
	/**
	 * Optional supplemental content rendered in the left text column, beneath the description.
	 * Use for helper/lock notes that belong with the label rather than crowding the control slot
	 * (the control column is intentionally narrow — see `controllerWrapper`/`textWrapper` styles).
	 */
	descriptionNote?: React.ReactNode;
	containerStyles?: StyleDeclarationValue[];
	titleStyles?: StyleDeclarationValue[];
	descriptionStyles?: StyleDeclarationValue[];
	textWrapperStyles?: StyleDeclarationValue[];
	controllerWrapperStyles?: StyleDeclarationValue[];
	error?: FieldError;
	showError?: boolean;
	children: (fieldProps: {
		value: any;
		onChange: (value: any) => void;
		onBlur: () => void;
		ref: React.Ref<any>;
	}) => React.ReactNode;
}

/**
 * A reusable wrapper component that handles React Hook Form field registration
 * and passes field props to its children. This eliminates boilerplate when
 * using form controls with custom components.
 *
 * Features:
 * - Built-in error handling and display
 * - Automatic field state management
 * - Consistent styling and layout
 * - Flexible error display options
 *
 * @example
 * ```tsx
 * <FormFieldWrapper
 *   name="isPaidEvent"
 *   control={control}
 *   title="Tiered Tickets"
 *   description="Enable the ability to sell tiers of tickets for this event."
 * >
 *   {({ value, onChange }) => (
 *     <Select
 *       value={value ? 'Paid Event' : 'Free Event'}
 *       onValueChange={(val) => onChange(val === 'Paid Event')}
 *     >
 *       <SelectItem value="Paid Event">Paid Event</SelectItem>
 *       <SelectItem value="Free Event">Free Event</SelectItem>
 *     </Select>
 *   )}
 * </FormFieldWrapper>
 * ```
 *
 * @example
 * ```tsx
 * <FormFieldWrapper
 *   name="isEnabled"
 *   control={control}
 *   title="Enable Feature"
 *   description="Turn this feature on or off"
 * >
 *   {({ value, onChange }) => (
 *     <Toggle
 *       isOn={value}
 *       onToggleCheckChanged={() => onChange(!value)}
 *     />
 *   )}
 * </FormFieldWrapper>
 * ```
 *
 * @example
 * ```tsx
 * // With custom error handling
 * <FormFieldWrapper
 *   name="eventTickets.0.name"
 *   control={control}
 *   title="Tier Name"
 *   description="Add a name for this tier of tickets"
 *   error={customError} // Override automatic error detection
 *   showError={true}    // Control error display (default: true)
 * >
 *   {({ value, onChange }) => (
 *     <Input
 *       value={value || ''}
 *       onChange={(e) => onChange(e.target.value)}
 *       placeholder="Basic Ticket"
 *     />
 *   )}
 * </FormFieldWrapper>
 * ```
 *
 * @example
 * ```tsx
 * // Disable error display
 * <FormFieldWrapper
 *   name="optionalField"
 *   control={control}
 *   title="Optional Field"
 *   showError={false} // Hide error messages
 * >
 *   {({ value, onChange }) => (
 *     <Input
 *       value={value || ''}
 *       onChange={(e) => onChange(e.target.value)}
 *     />
 *   )}
 * </FormFieldWrapper>
 * ```
 */
export const FormFieldWrapper = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	control,
	title,
	titleAdornment,
	description,
	descriptionNote,
	containerStyles = [],
	titleStyles = [],
	descriptionStyles = [],
	textWrapperStyles = [],
	controllerWrapperStyles = [],
	error,
	showError = true,
	children,
}: FormFieldWrapperProps<TFieldValues, TName>) => {
	// Generate a unique ID for the form control to associate with the label
	const controlId = React.useId();

	return (
		<div className={css(styles.container, ...containerStyles)}>
			<div className={css(styles.controllerWrapper, ...controllerWrapperStyles)}>
				<Controller
					name={name}
					control={control}
					render={({ field: { ref, ...fieldProps }, fieldState }) => {
						// Use provided error or fall back to fieldState error
						const displayError = error || fieldState.error;

						const childElement = children({
							value: fieldProps.value,
							onChange: fieldProps.onChange,
							onBlur: fieldProps.onBlur,
							ref,
						});

						// Only add the generated ID if the child doesn't already have one
						const finalId =
							childElement && React.isValidElement(childElement)
								? (childElement.props as any).id || controlId
								: controlId;

						const elementWithId =
							childElement && React.isValidElement(childElement)
								? React.cloneElement(childElement, {
										id: finalId,
									} as any)
								: childElement;

						return (
							<>
								{(title || description || descriptionNote) && (
									<div className={css(styles.textWrapper, textWrapperStyles)}>
										{title &&
											(titleAdornment ? (
												<div className={css(styles.titleRow)}>
													<label htmlFor={finalId} className={css(styles.title, titleStyles)}>
														{title}
													</label>
													{titleAdornment}
												</div>
											) : (
												<label htmlFor={finalId} className={css(styles.title, titleStyles)}>
													{title}
												</label>
											))}
										{description && <p className={css(styles.description, descriptionStyles)}>{description}</p>}
										{descriptionNote}
									</div>
								)}
								{elementWithId}
								{showError && displayError && <p className={css(styles.errorMessage)}>{displayError.message}</p>}
							</>
						);
					}}
				/>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: THEME.spacing.md,
		marginTop: THEME.spacing.md,
	},
	controllerWrapper: {
		width: '100%',
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.md,
	},
	textWrapper: {
		flex: 1,
		minWidth: '85%',
	},
	title: {
		fontSize: THEME.typography.fontSizes.md,
		margin: 0,
		marginBottom: THEME.spacing.xs,
		color: THEME.colors.text.main,
		cursor: 'pointer',
		display: 'block',
	},
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	description: {
		fontSize: THEME.typography.fontSizes.xs,
		margin: 0,
		color: THEME.colors.text.light,
		lineHeight: 1.4,
		fontWeight: THEME.typography.fontWeights.standard,
		marginBottom: `${THEME.spacing.xs}px`,
	},
	errorMessage: {
		color: THEME.colors.danger.main,
		fontSize: THEME.typography.fontSizes.xs,
		marginTop: THEME.spacing.xs,
		marginBottom: 0,
		width: '100%',
	},
});
